import {Action} from "@ngrx/store";
import {ForecastReviewActions} from "./import.actions";
import {ForecastMergeJobStatus} from "../generated-sources/api/forecast-import";

// Poll cadence + failure budget for the async forecast-merge job status polling (see
// pollForecastMergeJob in import.effects.ts). A merge runs for minutes, so a single flaky status
// request must not abort it: tolerate this many consecutive getMergeJobStatus failures
// (≈ MERGE_POLL_INTERVAL_MS apart) before giving up. The count resets on any successful read, and a
// genuine terminal job status (ERROR/TIMEOUT/CANCELLED) still errors out immediately.
export const MERGE_POLL_INTERVAL_MS = 2000;
export const MAX_CONSECUTIVE_POLL_FAILURES = 5;

// A single poll tick's outcome: either the job status was read, or the request itself failed transiently.
export type MergePollEvent =
    | { kind: "status"; status: ForecastMergeJobStatus }
    | { kind: "requestFailed"; error: string };

export interface MergePollState {
    failures: number;
    action: Action;
}

export const INITIAL_MERGE_POLL_STATE: MergePollState = {failures: 0, action: ForecastReviewActions.mergePolling()};

// Map a poll tick to the next action, carrying a running count of consecutive request failures so a
// transient blip keeps polling (mergePolling) rather than collapsing the merge into an error state.
// Kept as a pure reducer (rather than inline in the effect) so the consecutive-failure path — which the
// real-async-scheduler marble tests can't drive — is directly unit-testable.
export const reduceMergePoll = (acc: MergePollState, event: MergePollEvent): MergePollState => {
    if (event.kind === "status") {
        const status = event.status;
        if (status.status === "COMPLETED") {
            return {
                failures: 0,
                // COMPLETED means the merge committed server-side; a missing inline result is recoverable
                // by reloading (the import flips to MERGED and the outcome is fetched via loadMergeResult).
                action: status.result
                    ? ForecastReviewActions.mergeSuccess({result: status.result})
                    : ForecastReviewActions.mergeError({error: "Merge completed but returned no result.", recoverable: true})
            };
        }
        if (["ERROR", "TIMEOUT", "CANCELLED"].includes(status.status)) {
            // Terminal job failure: the merge did not commit, so this is not recoverable by reloading.
            return {failures: 0, action: ForecastReviewActions.mergeError({error: status.errorMessage ?? "The merge job failed."})};
        }
        return {failures: 0, action: ForecastReviewActions.mergePolling()};
    }
    // Transient status-request failure: keep polling until the budget is exhausted. Exhaustion is
    // indeterminate (the job may still be running or already committed), so it is recoverable by reloading.
    const failures = acc.failures + 1;
    return failures >= MAX_CONSECUTIVE_POLL_FAILURES
        ? {failures, action: ForecastReviewActions.mergeError({error: event.error, recoverable: true})}
        : {failures, action: ForecastReviewActions.mergePolling()};
};
